/**
 * Core logic hook for the Schedule Interview wizard.
 * Wraps the generic useStepValidation hook with scheduling-specific
 * mutations, mode configuration, edit-mode hydration, and save logic.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import type { TabItem } from '@kula-ai/components';
import { useShowToast } from '@/hooks/useShowToast';
import {
  useCreateInterview,
  useUpdateInterview,
  useGetInterview,
} from '@/api/useInterviews';
import {
  useCreateSelfSchedule,
  useUpdateSelfSchedule,
} from '@/api/useSelfSchedule';
import { useStepValidation, type StepFormApi } from '@/hooks/useStepValidation';
import type {
  ScheduleMode,
  ApiReference,
  ScheduleInterviewPayload,
  ScheduleModeConfig,
} from './config/schedule.types';
import { MODE_CONFIGS } from './config/modes';
import { SCHEDULE_INTERVIEW_COPY } from './config/schedule.copy';
import {
  buildSchedulePayload,
  buildSchedulePayloadFormData,
  hydratePayloadFromApi,
} from './payload';
import { useInterviewPlan } from './api/useInterviewPlan';
import { api, ApiError } from '@/api/request';
import { getErrorMessage } from '@/utils/errors';

interface PhoneValue {
  countryCode: string;
  dialCode: string;
  number: string;
}

interface UseScheduleInterviewOptions {
  mode: ScheduleMode;
  apiReference: ApiReference;
  applicationId?: number;
  candidateId?: number;
  jobId?: number;
  stageId?: number;
  stageActivityId?: number;
  interviewId?: number;
  scheduleId?: number;
  /**
   * Candidate's current phone-on-record (from applicationData.contact).
   * Used to detect whether the user edited the phone on the Details
   * step so we can fire `PUT /api/v1/contacts/:id` only when it
   * actually changed — a view of the existing number shouldn't
   * generate a contact update.
   */
  initialCandidatePhone?: PhoneValue;
  onClose?: () => void;
  onSuccess?: () => void;
}

function phoneValuesEqual(
  a: PhoneValue | null | undefined,
  b: PhoneValue | null | undefined
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    (a.dialCode ?? '').trim() === (b.dialCode ?? '').trim() &&
    (a.number ?? '').trim() === (b.number ?? '').trim()
  );
}

export function useScheduleInterview({
  mode,
  apiReference,
  applicationId,
  candidateId,
  jobId,
  stageId,
  stageActivityId,
  interviewId,
  scheduleId,
  initialCandidatePhone,
  onClose,
  onSuccess,
}: UseScheduleInterviewOptions) {
  const toast = useShowToast();
  const config: ScheduleModeConfig = MODE_CONFIGS[mode];
  const isEdit = apiReference === 'edit';
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  // ---- Edit-mode GET ----
  // TODO(self-schedule revamp): when mode is self-schedule and scheduleId
  // is present, edit should hydrate from GET /ats_schedules/:id (flat
  // self-schedule shape — time_slots, interview_kind, contact, mail
  // bundle) rather than the interview show view. Tracked in the
  // backend-audit memory + sch-10 follow-ups.
  const {
    data: existingInterview,
    isLoading: isLoadingExisting,
    isError: isLoadError,
    isFetched: isFetchedExisting,
  } = useGetInterview(interviewId, {
    enabled: isEdit && !!interviewId,
  });

  // ---- Payload ref + edit hydration ----
  const payloadRef = useRef<ScheduleInterviewPayload>({
    details: {},
    communication: {},
    scorecard: {},
  });

  const lastHydratedIdRef = useRef<number | null>(null);
  const prefillGenerationRef = useRef(0);

  if (!isEdit) {
    lastHydratedIdRef.current = null;
  }

  const editDataReady =
    isEdit &&
    existingInterview !== undefined &&
    isFetchedExisting &&
    !isLoadingExisting;

  if (editDataReady && existingInterview.id !== lastHydratedIdRef.current) {
    lastHydratedIdRef.current = existingInterview.id;
    prefillGenerationRef.current += 1;
    payloadRef.current = hydratePayloadFromApi(existingInterview);
  }

  const prefillVersion = isEdit ? prefillGenerationRef.current : 0;

  useEffect(() => {
    if (!isLoadError || !isEdit) return;
    toast({
      title: 'Could not load interview',
      description: 'Try closing and opening the editor again.',
      status: 'error',
    });
  }, [isEdit, isLoadError, toast]);

  // ---- Interview plan defaults (non-adhoc create) ----
  const isAdhoc = mode === 'adhoc' || mode === 'self-schedule-adhoc';
  const { data: interviewPlanFromNew } = useInterviewPlan(
    {
      stageId: stageId ?? 0,
      stageActivityId: stageActivityId ?? undefined,
      contactId: candidateId ?? undefined,
    },
    { enabled: !isAdhoc && !isEdit && !!stageId && stageId > 0 }
  );

  /** Create: `ats_interviews/new`. Edit: merge GET interview so Details
   *  / Schedule schema can read the same `plan.*` fields it reads on
   *  create (name/kind/location/participants/calendar_*). The plan
   *  endpoint is disabled in edit mode so `interviewPlanFromNew` is
   *  undefined here — existingInterview is the source of truth. */
  const interviewPlan = useMemo((): Record<string, unknown> | undefined => {
    if (!isEdit || !existingInterview) {
      return interviewPlanFromNew;
    }
    const ex = existingInterview as Record<string, unknown>;
    const base = (interviewPlanFromNew ?? {}) as Record<string, unknown>;
    return { ...ex, ...base };
  }, [interviewPlanFromNew, isEdit, existingInterview]);

  // ---- Step refs ----
  const stepRefsMap = useRef<Record<string, RefObject<StepFormApi | null>>>({});

  const getStepRef = useCallback(
    (stepKey: string): RefObject<StepFormApi | null> => {
      if (!stepRefsMap.current[stepKey]) {
        stepRefsMap.current[stepKey] = { current: null };
      }
      return stepRefsMap.current[stepKey];
    },
    []
  );

  const stepRefs = useMemo(() => {
    const refs: Record<string, RefObject<StepFormApi | null>> = {};
    for (const step of config.steps) {
      refs[step.key] = getStepRef(step.key);
    }
    return refs;
  }, [config.steps, getStepRef]);

  // ---- Mutations ----
  const createInterview = useCreateInterview();
  const updateInterview = useUpdateInterview();
  const createSelfSchedule = useCreateSelfSchedule();
  const updateSelfSchedule = useUpdateSelfSchedule();

  const isSaving =
    createInterview.isPending ||
    updateInterview.isPending ||
    createSelfSchedule.isPending ||
    updateSelfSchedule.isPending;

  // ---- Save (onComplete callback for useStepValidation) ----
  const handleComplete = useCallback(
    async (payload: ScheduleInterviewPayload) => {
      const contextIds = {
        ats_application_id: applicationId,
        ats_job_id: jobId,
        ats_job_stage_id: stageId,
        ats_job_stage_action_id: stageActivityId ?? null,
        candidate_id: candidateId,
      };
      // /ats_interviews wants multipart so
      // `candidate_email_comm_config.attachments[]` can carry raw
      // `File` blobs (CORE-4040 backend). /ats_schedules does its own
      // `convertToFormData` inside `useSelfSchedule`, so we hand it
      // the plain object to avoid double-serialisation.
      const interviewFormData = buildSchedulePayloadFormData(
        payload,
        contextIds,
        mode
      );
      const selfScheduleJson = buildSchedulePayload(payload, contextIds, mode);

      try {
        // Phone edit → contact update. Only fires when the user
        // actually opened the pencil AND the new value differs from
        // what came in via `initialCandidatePhone`. A read-only
        // glance at the existing number skips this. `candidate_phone`
        // itself stays stripped from the interview payload via
        // `UI_ONLY_FIELDS` — `/ats_interviews` doesn't accept it.
        const editedPhone = payload.details.candidate_phone as
          | PhoneValue
          | null
          | undefined;
        const phoneWasEdited =
          payload.details.isCandidatePhoneEditing === 'edit' &&
          !phoneValuesEqual(editedPhone, initialCandidatePhone);
        if (
          phoneWasEdited &&
          typeof candidateId === 'number' &&
          candidateId > 0 &&
          editedPhone?.number
        ) {
          await api.put(
            `/contacts/${candidateId}`,
            {
              phone_code: editedPhone.dialCode ?? '',
              phone_number: editedPhone.number ?? '',
            },
            { client: 'api' }
          );
        }

        if (isSelfSchedule) {
          if (isEdit && scheduleId) {
            await updateSelfSchedule.mutateAsync({
              scheduleId,
              payload: selfScheduleJson,
            });
          } else {
            await createSelfSchedule.mutateAsync({
              payload: selfScheduleJson,
            });
          }
        } else {
          if (isEdit && interviewId) {
            await updateInterview.mutateAsync({
              interviewId,
              payload: interviewFormData,
            });
          } else {
            await createInterview.mutateAsync({
              payload: interviewFormData,
            });
          }
        }

        toast({
          title: isEdit
            ? SCHEDULE_INTERVIEW_COPY.toast.updateSuccess.title
            : SCHEDULE_INTERVIEW_COPY.toast.createSuccess.title,
          status: 'success',
        });
        onSuccess?.();
        onClose?.();
      } catch (err) {
        // Surface backend error codes via the shared `scheduler` error
        // map so users get actionable messages (e.g.
        // `err_interviewer_email_sender_mailbox_not_connected` → "The
        // selected 'From' user hasn't connected their mailbox…").
        // Falls back to the generic copy only when the map doesn't
        // know the code.
        const errors =
          err instanceof ApiError && err.errors.length > 0
            ? err.errors
            : undefined;
        const description = errors
          ? getErrorMessage({ errors }, 'scheduler')
          : SCHEDULE_INTERVIEW_COPY.toast.saveError.fallback;
        toast({
          title: SCHEDULE_INTERVIEW_COPY.toast.saveError.title,
          description,
          status: 'error',
        });
      }
    },
    [
      mode,
      applicationId,
      candidateId,
      initialCandidatePhone,
      jobId,
      stageId,
      stageActivityId,
      createInterview,
      createSelfSchedule,
      interviewId,
      isEdit,
      isSelfSchedule,
      onClose,
      onSuccess,
      scheduleId,
      toast,
      updateInterview,
      updateSelfSchedule,
    ]
  );

  // ---- Build TabItems from mode config ----
  const tabItems = useMemo(
    (): TabItem[] =>
      config.steps.map((step) => ({
        label: step.label,
        value: step.key,
      })),
    [config.steps]
  );

  // ---- Step validation orchestration ----
  const finalActionLabel = isEdit
    ? SCHEDULE_INTERVIEW_COPY.footer.saveInterview
    : SCHEDULE_INTERVIEW_COPY.footer.scheduleInterview;

  const stepper = useStepValidation<ScheduleInterviewPayload>({
    tabItems,
    payloadRef,
    stepRefs,
    onComplete: handleComplete,
    finalActionLabel,
    nextPrefix: 'Next:',
    backPrefix: 'Back:',
    enableBack: true,
    wizardCompleteMode: 'validateAllSteps',
    activateFirstInvalidStepOnWizardComplete: true,
  });

  // ---- "Schedule Now" secondary CTA ----
  const scheduleNow = useCallback(() => {
    void stepper.goNextOrComplete();
  }, [stepper]);

  return {
    config,
    payloadRef,
    getStepRef,
    isSaving,
    scheduleNow,
    prefillVersion,
    interviewPlan,
    showEditLoader: isEdit && isLoadingExisting,
    showEditError: isEdit && isLoadError,
    // From useStepValidation
    tabItems: stepper.tabItems,
    activeStep: stepper.activeStep,
    isBusy: stepper.isBusy,
    onStepChange: stepper.onStepChange,
    goNextOrComplete: stepper.goNextOrComplete,
    goBack: stepper.goBack,
    canGoBack: stepper.canGoBack,
    primaryButtonLabel: stepper.primaryButtonLabel,
    backButtonLabel: stepper.backButtonLabel,
  };
}
